import { useState } from "react";
import AdminLayout from "@/components/admin/AdminLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useSiteConfig } from "@/hooks/useSiteConfig";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Save } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const AdminPageContent = () => {
  const { getConfig, loading, refetch } = useSiteConfig();
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);

  const handleSaveConfig = async (key: string, value: any) => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('site_config')
        .upsert({
          key,
          value: typeof value === 'string' ? value : JSON.stringify(value)
        }, {
          onConflict: 'key'
        });

      if (error) throw error;

      toast({
        title: "Guardado exitosamente",
        description: "El contenido ha sido actualizado.",
      });
    } catch (error) {
      console.error('Error saving config:', error);
      toast({
        title: "Error al guardar",
        description: "Hubo un problema al guardar los cambios.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSection = async (section: string, fields: { [key: string]: any }) => {
    setSaving(true);
    try {
      const updates = Object.entries(fields).map(([key, value]) => ({
        key,
        value: typeof value === 'string' ? value : JSON.stringify(value)
      }));

      const { error } = await supabase
        .from('site_config')
        .upsert(updates, {
          onConflict: 'key'
        });

      if (error) throw error;

      toast({
        title: "Sección guardada",
        description: `La sección ${section} ha sido actualizada.`,
      });
      
      // Refetch config to see changes
      refetch();
    } catch (error) {
      console.error('Error saving section:', error);
      toast({
        title: "Error al guardar",
        description: "Hubo un problema al guardar los cambios.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Contenido de Páginas</h1>
          <p className="text-muted-foreground">Edita el contenido de las diferentes secciones de tu sitio web</p>
        </div>

        <Tabs defaultValue="home" className="space-y-6">
          <TabsList>
            <TabsTrigger value="home">Home</TabsTrigger>
            <TabsTrigger value="about">About</TabsTrigger>
            <TabsTrigger value="contact">Contact</TabsTrigger>
          </TabsList>

          <TabsContent value="home" className="space-y-6">
            <Tabs defaultValue="tours" className="space-y-6">
              <TabsList className="grid grid-cols-6 w-full">
                <TabsTrigger value="tours">Tours</TabsTrigger>
                <TabsTrigger value="about">Nosotros</TabsTrigger>
                <TabsTrigger value="why">Ventajas</TabsTrigger>
                <TabsTrigger value="services">Servicios</TabsTrigger>
                <TabsTrigger value="airports">Aeropuertos</TabsTrigger>
                <TabsTrigger value="faq">FAQ</TabsTrigger>
              </TabsList>

              {/* Popular Tours Section */}
              <TabsContent value="tours">
                <Card>
                  <CardHeader>
                    <CardTitle>Sección de Tours Populares</CardTitle>
                    <CardDescription>Edita el contenido de la sección de tours populares</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label htmlFor="tours_badge">Badge</Label>
                      <Input
                        id="tours_badge"
                        defaultValue={getConfig('home_tours_badge', 'Popular Tours')}
                        onBlur={(e) => handleSaveConfig('home_tours_badge', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="tours_title">Título</Label>
                      <Input
                        id="tours_title"
                        defaultValue={getConfig('home_tours_title', 'Discover Punta Cana')}
                        onBlur={(e) => handleSaveConfig('home_tours_title', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="tours_description">Descripción</Label>
                      <Textarea
                        id="tours_description"
                        defaultValue={getConfig('home_tours_description', 'Explore the most amazing tours...')}
                        onBlur={(e) => handleSaveConfig('home_tours_description', e.target.value)}
                        rows={3}
                      />
                    </div>
                    <Badge variant="outline" className="mt-4">Los cambios se guardan automáticamente al salir del campo</Badge>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* About Section */}
              <TabsContent value="about">
                <Card>
                  <CardHeader>
                    <CardTitle>Sección "Quiénes Somos"</CardTitle>
                    <CardDescription>Edita el contenido de la sección sobre nosotros</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label htmlFor="about_badge">Badge</Label>
                      <Input
                        id="about_badge"
                        defaultValue={getConfig('home_about_badge', 'About Us')}
                        onBlur={(e) => handleSaveConfig('home_about_badge', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="about_title">Título</Label>
                      <Input
                        id="about_title"
                        defaultValue={getConfig('home_about_title', 'Who We Are')}
                        onBlur={(e) => handleSaveConfig('home_about_title', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="about_description_1">Primer Párrafo</Label>
                      <Textarea
                        id="about_description_1"
                        defaultValue={getConfig('home_about_description_1', '')}
                        onBlur={(e) => handleSaveConfig('home_about_description_1', e.target.value)}
                        rows={4}
                      />
                    </div>
                    <div>
                      <Label htmlFor="about_description_2">Segundo Párrafo</Label>
                      <Textarea
                        id="about_description_2"
                        defaultValue={getConfig('home_about_description_2', '')}
                        onBlur={(e) => handleSaveConfig('home_about_description_2', e.target.value)}
                        rows={4}
                      />
                    </div>
                    <div>
                      <Label htmlFor="about_button">Texto del Botón</Label>
                      <Input
                        id="about_button"
                        defaultValue={getConfig('home_about_button_text', 'Learn More About Us')}
                        onBlur={(e) => handleSaveConfig('home_about_button_text', e.target.value)}
                      />
                    </div>
                    <Badge variant="outline" className="mt-4">Los cambios se guardan automáticamente al salir del campo</Badge>
                  </CardContent>
                </Card>

                <Card className="mt-6">
                  <CardHeader>
                    <CardTitle>Estadísticas</CardTitle>
                    <CardDescription>Edita las 4 tarjetas de estadísticas (JSON)</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div>
                      <Label htmlFor="about_stats">Datos JSON de Estadísticas</Label>
                      <Textarea
                        id="about_stats"
                        key={`about_stats_${JSON.stringify(getConfig('home_about_stats', []))}`}
                        defaultValue={JSON.stringify(getConfig('home_about_stats', []), null, 2)}
                        onBlur={(e) => {
                          try {
                            const parsed = JSON.parse(e.target.value);
                            handleSaveConfig('home_about_stats', parsed);
                          } catch (error) {
                            toast({
                              title: "Error de formato",
                              description: "El JSON no es válido",
                              variant: "destructive",
                            });
                          }
                        }}
                        rows={15}
                        className="font-mono text-sm"
                      />
                      <p className="text-sm text-muted-foreground mt-2">
                        Formato: Array de objetos con icon, value, label, color
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Why Choose Us Section */}
              <TabsContent value="why">
                <Card>
                  <CardHeader>
                    <CardTitle>Sección "Por Qué Elegirnos"</CardTitle>
                    <CardDescription>Edita el contenido de las ventajas</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label htmlFor="why_badge">Badge</Label>
                      <Input
                        id="why_badge"
                        defaultValue={getConfig('home_why_badge', 'Our Advantages')}
                        onBlur={(e) => handleSaveConfig('home_why_badge', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="why_title">Título</Label>
                      <Input
                        id="why_title"
                        defaultValue={getConfig('home_why_title', 'Why Choose Us')}
                        onBlur={(e) => handleSaveConfig('home_why_title', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="why_description">Descripción</Label>
                      <Textarea
                        id="why_description"
                        defaultValue={getConfig('home_why_description', '')}
                        onBlur={(e) => handleSaveConfig('home_why_description', e.target.value)}
                        rows={2}
                      />
                    </div>
                  </CardContent>
                </Card>

                <Card className="mt-6">
                  <CardHeader>
                    <CardTitle>Items de Ventajas</CardTitle>
                    <CardDescription>Edita los 6 items de ventajas (JSON)</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div>
                      <Label htmlFor="why_items">Datos JSON de Items</Label>
                      <Textarea
                        id="why_items"
                        key={`why_items_${JSON.stringify(getConfig('home_why_items', []))}`}
                        defaultValue={JSON.stringify(getConfig('home_why_items', []), null, 2)}
                        onBlur={(e) => {
                          try {
                            const parsed = JSON.parse(e.target.value);
                            handleSaveConfig('home_why_items', parsed);
                          } catch (error) {
                            toast({
                              title: "Error de formato",
                              description: "El JSON no es válido",
                              variant: "destructive",
                            });
                          }
                        }}
                        rows={25}
                        className="font-mono text-sm"
                      />
                      <p className="text-sm text-muted-foreground mt-2">
                        Formato: Array de objetos con icon, title, description, color
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Services Section */}
              <TabsContent value="services">
                <Card>
                  <CardHeader>
                    <CardTitle>Sección "Nuestros Servicios"</CardTitle>
                    <CardDescription>Edita el contenido de la sección de servicios</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label htmlFor="services_badge">Badge</Label>
                      <Input
                        id="services_badge"
                        defaultValue={getConfig('home_services_badge', 'What We Offer')}
                        onBlur={(e) => handleSaveConfig('home_services_badge', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="services_title">Título</Label>
                      <Input
                        id="services_title"
                        defaultValue={getConfig('home_services_title', 'Our Services')}
                        onBlur={(e) => handleSaveConfig('home_services_title', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="services_description">Descripción</Label>
                      <Textarea
                        id="services_description"
                        defaultValue={getConfig('home_services_description', '')}
                        onBlur={(e) => handleSaveConfig('home_services_description', e.target.value)}
                        rows={2}
                      />
                    </div>

                    <div className="border-t pt-4 mt-6">
                      <h4 className="font-semibold mb-4">Servicio de Transfers</h4>
                      <div className="space-y-4">
                        <div>
                          <Label htmlFor="services_transfer_title">Título</Label>
                          <Input
                            id="services_transfer_title"
                            defaultValue={getConfig('home_services_transfer_title', 'Airport Transfers')}
                            onBlur={(e) => handleSaveConfig('home_services_transfer_title', e.target.value)}
                          />
                        </div>
                        <div>
                          <Label htmlFor="services_transfer_description">Descripción</Label>
                          <Textarea
                            id="services_transfer_description"
                            defaultValue={getConfig('home_services_transfer_description', '')}
                            onBlur={(e) => handleSaveConfig('home_services_transfer_description', e.target.value)}
                            rows={3}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="border-t pt-4 mt-6">
                      <h4 className="font-semibold mb-4">Servicio de Tours</h4>
                      <div className="space-y-4">
                        <div>
                          <Label htmlFor="services_tours_title">Título</Label>
                          <Input
                            id="services_tours_title"
                            defaultValue={getConfig('home_services_tours_title', 'Tours & Excursions')}
                            onBlur={(e) => handleSaveConfig('home_services_tours_title', e.target.value)}
                          />
                        </div>
                        <div>
                          <Label htmlFor="services_tours_description">Descripción</Label>
                          <Textarea
                            id="services_tours_description"
                            defaultValue={getConfig('home_services_tours_description', '')}
                            onBlur={(e) => handleSaveConfig('home_services_tours_description', e.target.value)}
                            rows={3}
                          />
                        </div>
                      </div>
                    </div>

                    <Badge variant="outline" className="mt-4">Los cambios se guardan automáticamente al salir del campo</Badge>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Airports Section */}
              <TabsContent value="airports">
                <Card>
                  <CardHeader>
                    <CardTitle>Sección de Aeropuertos</CardTitle>
                    <CardDescription>Edita el contenido de la sección de aeropuertos</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label htmlFor="airports_badge">Badge</Label>
                      <Input
                        id="airports_badge"
                        defaultValue={getConfig('home_airports_badge', 'We Serve All Airports')}
                        onBlur={(e) => handleSaveConfig('home_airports_badge', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="airports_title">Título</Label>
                      <Input
                        id="airports_title"
                        defaultValue={getConfig('home_airports_title', 'Dominican Republic Airports')}
                        onBlur={(e) => handleSaveConfig('home_airports_title', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="airports_description">Descripción</Label>
                      <Textarea
                        id="airports_description"
                        defaultValue={getConfig('home_airports_description', '')}
                        onBlur={(e) => handleSaveConfig('home_airports_description', e.target.value)}
                        rows={2}
                      />
                    </div>
                    <Badge variant="outline" className="mt-4">Los cambios se guardan automáticamente al salir del campo</Badge>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* FAQ Section */}
              <TabsContent value="faq">
                <Card>
                  <CardHeader>
                    <CardTitle>Sección de Preguntas Frecuentes</CardTitle>
                    <CardDescription>Edita el contenido de la sección de FAQ</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label htmlFor="faq_badge">Badge</Label>
                      <Input
                        id="faq_badge"
                        defaultValue={getConfig('home_faq_badge', 'Help Center')}
                        onBlur={(e) => handleSaveConfig('home_faq_badge', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="faq_title">Título</Label>
                      <Input
                        id="faq_title"
                        defaultValue={getConfig('home_faq_title', 'Frequently Asked Questions')}
                        onBlur={(e) => handleSaveConfig('home_faq_title', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="faq_description">Descripción</Label>
                      <Textarea
                        id="faq_description"
                        defaultValue={getConfig('home_faq_description', '')}
                        onBlur={(e) => handleSaveConfig('home_faq_description', e.target.value)}
                        rows={2}
                      />
                    </div>
                    <Badge variant="outline" className="mt-4">Los cambios se guardan automáticamente al salir del campo</Badge>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </TabsContent>

          {/* About Page */}
          <TabsContent value="about" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Hero Section</CardTitle>
                <CardDescription>Edita el contenido del hero de About</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="about_hero_badge">Badge</Label>
                  <Input
                    id="about_hero_badge"
                    defaultValue={getConfig('about_hero_badge', 'Your Premier Transfer & Tour Service')}
                    onBlur={(e) => handleSaveConfig('about_hero_badge', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="about_hero_title">Título</Label>
                  <Input
                    id="about_hero_title"
                    defaultValue={getConfig('about_hero_title', 'About TROPUNCA VIP')}
                    onBlur={(e) => handleSaveConfig('about_hero_title', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="about_hero_description">Descripción</Label>
                  <Textarea
                    id="about_hero_description"
                    defaultValue={getConfig('about_hero_description', '')}
                    onBlur={(e) => handleSaveConfig('about_hero_description', e.target.value)}
                    rows={3}
                  />
                </div>
                <Badge variant="outline" className="mt-4">Los cambios se guardan automáticamente al salir del campo</Badge>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Estadísticas</CardTitle>
                <CardDescription>Edita las 4 tarjetas de estadísticas (JSON)</CardDescription>
              </CardHeader>
              <CardContent>
                <div>
                  <Label htmlFor="about_stats">Datos JSON de Estadísticas</Label>
                  <Textarea
                    id="about_stats"
                    key={`about_stats_${JSON.stringify(getConfig('about_stats', []))}`}
                    defaultValue={JSON.stringify(getConfig('about_stats', []), null, 2)}
                    onBlur={(e) => {
                      try {
                        const parsed = JSON.parse(e.target.value);
                        handleSaveConfig('about_stats', parsed);
                      } catch (error) {
                        toast({
                          title: "Error de formato",
                          description: "El JSON no es válido",
                          variant: "destructive",
                        });
                      }
                    }}
                    rows={15}
                    className="font-mono text-sm"
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Nuestra Historia</CardTitle>
                <CardDescription>Edita el contenido de la sección "Our Story"</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="about_story_badge">Badge</Label>
                  <Input
                    id="about_story_badge"
                    defaultValue={getConfig('about_story_badge', 'Our Story')}
                    onBlur={(e) => handleSaveConfig('about_story_badge', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="about_story_title">Título</Label>
                  <Input
                    id="about_story_title"
                    defaultValue={getConfig('about_story_title', '')}
                    onBlur={(e) => handleSaveConfig('about_story_title', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="about_story_paragraph_1">Párrafo 1</Label>
                  <Textarea
                    id="about_story_paragraph_1"
                    defaultValue={getConfig('about_story_paragraph_1', '')}
                    onBlur={(e) => handleSaveConfig('about_story_paragraph_1', e.target.value)}
                    rows={4}
                  />
                </div>
                <div>
                  <Label htmlFor="about_story_paragraph_2">Párrafo 2</Label>
                  <Textarea
                    id="about_story_paragraph_2"
                    defaultValue={getConfig('about_story_paragraph_2', '')}
                    onBlur={(e) => handleSaveConfig('about_story_paragraph_2', e.target.value)}
                    rows={4}
                  />
                </div>
                <div>
                  <Label htmlFor="about_story_paragraph_3">Párrafo 3</Label>
                  <Textarea
                    id="about_story_paragraph_3"
                    defaultValue={getConfig('about_story_paragraph_3', '')}
                    onBlur={(e) => handleSaveConfig('about_story_paragraph_3', e.target.value)}
                    rows={4}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Misión y Visión</CardTitle>
                <CardDescription>Edita los textos de misión y visión</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="border-b pb-4">
                  <h4 className="font-semibold mb-4">Misión</h4>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="about_mission_title">Título</Label>
                      <Input
                        id="about_mission_title"
                        defaultValue={getConfig('about_mission_title', 'Our Mission')}
                        onBlur={(e) => handleSaveConfig('about_mission_title', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="about_mission_text">Texto</Label>
                      <Textarea
                        id="about_mission_text"
                        defaultValue={getConfig('about_mission_text', '')}
                        onBlur={(e) => handleSaveConfig('about_mission_text', e.target.value)}
                        rows={4}
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold mb-4">Visión</h4>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="about_vision_title">Título</Label>
                      <Input
                        id="about_vision_title"
                        defaultValue={getConfig('about_vision_title', 'Our Vision')}
                        onBlur={(e) => handleSaveConfig('about_vision_title', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="about_vision_text">Texto</Label>
                      <Textarea
                        id="about_vision_text"
                        defaultValue={getConfig('about_vision_text', '')}
                        onBlur={(e) => handleSaveConfig('about_vision_text', e.target.value)}
                        rows={4}
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Valores</CardTitle>
                <CardDescription>Edita la sección de valores</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="about_values_badge">Badge</Label>
                  <Input
                    id="about_values_badge"
                    defaultValue={getConfig('about_values_badge', 'Our Values')}
                    onBlur={(e) => handleSaveConfig('about_values_badge', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="about_values_title">Título</Label>
                  <Input
                    id="about_values_title"
                    defaultValue={getConfig('about_values_title', '')}
                    onBlur={(e) => handleSaveConfig('about_values_title', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="about_values_description">Descripción</Label>
                  <Textarea
                    id="about_values_description"
                    defaultValue={getConfig('about_values_description', '')}
                    onBlur={(e) => handleSaveConfig('about_values_description', e.target.value)}
                    rows={2}
                  />
                </div>
                <div>
                  <Label htmlFor="about_values">Items de Valores (JSON)</Label>
                  <Textarea
                    id="about_values"
                    key={`about_values_${JSON.stringify(getConfig('about_values', []))}`}
                    defaultValue={JSON.stringify(getConfig('about_values', []), null, 2)}
                    onBlur={(e) => {
                      try {
                        const parsed = JSON.parse(e.target.value);
                        handleSaveConfig('about_values', parsed);
                      } catch (error) {
                        toast({
                          title: "Error de formato",
                          description: "El JSON no es válido",
                          variant: "destructive",
                        });
                      }
                    }}
                    rows={15}
                    className="font-mono text-sm"
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Equipo</CardTitle>
                <CardDescription>Edita la sección de equipo</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="about_team_badge">Badge</Label>
                  <Input
                    id="about_team_badge"
                    defaultValue={getConfig('about_team_badge', 'Our Team')}
                    onBlur={(e) => handleSaveConfig('about_team_badge', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="about_team_title">Título</Label>
                  <Input
                    id="about_team_title"
                    defaultValue={getConfig('about_team_title', '')}
                    onBlur={(e) => handleSaveConfig('about_team_title', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="about_team_description">Descripción</Label>
                  <Textarea
                    id="about_team_description"
                    defaultValue={getConfig('about_team_description', '')}
                    onBlur={(e) => handleSaveConfig('about_team_description', e.target.value)}
                    rows={2}
                  />
                </div>
                <div>
                  <Label htmlFor="about_team">Miembros del Equipo (JSON)</Label>
                  <Textarea
                    id="about_team"
                    key={`about_team_${JSON.stringify(getConfig('about_team', []))}`}
                    defaultValue={JSON.stringify(getConfig('about_team', []), null, 2)}
                    onBlur={(e) => {
                      try {
                        const parsed = JSON.parse(e.target.value);
                        handleSaveConfig('about_team', parsed);
                      } catch (error) {
                        toast({
                          title: "Error de formato",
                          description: "El JSON no es válido",
                          variant: "destructive",
                        });
                      }
                    }}
                    rows={12}
                    className="font-mono text-sm"
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Por Qué Elegirnos</CardTitle>
                <CardDescription>Edita la sección "Why Choose Us"</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="about_why_badge">Badge</Label>
                  <Input
                    id="about_why_badge"
                    defaultValue={getConfig('about_why_badge', 'Why Choose Us')}
                    onBlur={(e) => handleSaveConfig('about_why_badge', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="about_why_title">Título</Label>
                  <Input
                    id="about_why_title"
                    defaultValue={getConfig('about_why_title', '')}
                    onBlur={(e) => handleSaveConfig('about_why_title', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="about_why_description">Descripción</Label>
                  <Textarea
                    id="about_why_description"
                    defaultValue={getConfig('about_why_description', '')}
                    onBlur={(e) => handleSaveConfig('about_why_description', e.target.value)}
                    rows={2}
                  />
                </div>
                <div>
                  <Label htmlFor="about_why_items">Items (JSON Array)</Label>
                  <Textarea
                    id="about_why_items"
                    key={`about_why_items_${JSON.stringify(getConfig('about_why_items', []))}`}
                    defaultValue={JSON.stringify(getConfig('about_why_items', []), null, 2)}
                    onBlur={(e) => {
                      try {
                        const parsed = JSON.parse(e.target.value);
                        handleSaveConfig('about_why_items', parsed);
                      } catch (error) {
                        toast({
                          title: "Error de formato",
                          description: "El JSON no es válido",
                          variant: "destructive",
                        });
                      }
                    }}
                    rows={12}
                    className="font-mono text-sm"
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>CTA y Contacto</CardTitle>
                <CardDescription>Edita la sección de llamado a la acción y contacto</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="about_cta_title">Título CTA</Label>
                  <Input
                    id="about_cta_title"
                    defaultValue={getConfig('about_cta_title', '')}
                    onBlur={(e) => handleSaveConfig('about_cta_title', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="about_cta_description">Descripción CTA</Label>
                  <Textarea
                    id="about_cta_description"
                    defaultValue={getConfig('about_cta_description', '')}
                    onBlur={(e) => handleSaveConfig('about_cta_description', e.target.value)}
                    rows={2}
                  />
                </div>
                <div>
                  <Label htmlFor="about_contact_phone">Teléfono</Label>
                  <Input
                    id="about_contact_phone"
                    defaultValue={getConfig('about_contact_phone', '')}
                    onBlur={(e) => handleSaveConfig('about_contact_phone', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="about_contact_email">Email</Label>
                  <Input
                    id="about_contact_email"
                    defaultValue={getConfig('about_contact_email', '')}
                    onBlur={(e) => handleSaveConfig('about_contact_email', e.target.value)}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Contact Page */}
          <TabsContent value="contact" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Hero Section</CardTitle>
                <CardDescription>Edita el contenido del hero de Contact</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="contact_hero_badge">Badge</Label>
                  <Input
                    id="contact_hero_badge"
                    defaultValue={getConfig('contact_hero_badge', 'Contact Us')}
                    onBlur={(e) => handleSaveConfig('contact_hero_badge', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="contact_hero_title">Título</Label>
                  <Input
                    id="contact_hero_title"
                    defaultValue={getConfig('contact_hero_title', "Let's Connect")}
                    onBlur={(e) => handleSaveConfig('contact_hero_title', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="contact_hero_description">Descripción</Label>
                  <Textarea
                    id="contact_hero_description"
                    defaultValue={getConfig('contact_hero_description', '')}
                    onBlur={(e) => handleSaveConfig('contact_hero_description', e.target.value)}
                    rows={3}
                  />
                </div>
                <Badge variant="outline" className="mt-4">Los cambios se guardan automáticamente al salir del campo</Badge>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Información de Contacto</CardTitle>
                <CardDescription>Edita la sección de información de contacto</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="contact_info_badge">Badge</Label>
                  <Input
                    id="contact_info_badge"
                    defaultValue={getConfig('contact_info_badge', 'Contact Information')}
                    onBlur={(e) => handleSaveConfig('contact_info_badge', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="contact_info_title">Título</Label>
                  <Input
                    id="contact_info_title"
                    defaultValue={getConfig('contact_info_title', "We're Here to Help")}
                    onBlur={(e) => handleSaveConfig('contact_info_title', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="contact_info_description">Descripción</Label>
                  <Textarea
                    id="contact_info_description"
                    defaultValue={getConfig('contact_info_description', '')}
                    onBlur={(e) => handleSaveConfig('contact_info_description', e.target.value)}
                    rows={3}
                  />
                </div>

                <div className="border-t pt-4 mt-6">
                  <h4 className="font-semibold mb-4">Datos de Contacto</h4>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="contact_phone">Teléfono</Label>
                      <Input
                        id="contact_phone"
                        defaultValue={getConfig('contact_phone', '')}
                        onBlur={(e) => handleSaveConfig('contact_phone', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="contact_phone_description">Descripción del Teléfono</Label>
                      <Input
                        id="contact_phone_description"
                        defaultValue={getConfig('contact_phone_description', '')}
                        onBlur={(e) => handleSaveConfig('contact_phone_description', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="contact_email">Email</Label>
                      <Input
                        id="contact_email"
                        defaultValue={getConfig('contact_email', '')}
                        onBlur={(e) => handleSaveConfig('contact_email', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="contact_email_description">Descripción del Email</Label>
                      <Input
                        id="contact_email_description"
                        defaultValue={getConfig('contact_email_description', '')}
                        onBlur={(e) => handleSaveConfig('contact_email_description', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="contact_location">Ubicación</Label>
                      <Input
                        id="contact_location"
                        defaultValue={getConfig('contact_location', '')}
                        onBlur={(e) => handleSaveConfig('contact_location', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="contact_location_description">Descripción de Ubicación</Label>
                      <Input
                        id="contact_location_description"
                        defaultValue={getConfig('contact_location_description', '')}
                        onBlur={(e) => handleSaveConfig('contact_location_description', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Formulario de Contacto</CardTitle>
                <CardDescription>Edita la sección del formulario</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="contact_form_badge">Badge</Label>
                  <Input
                    id="contact_form_badge"
                    defaultValue={getConfig('contact_form_badge', 'Send Us a Message')}
                    onBlur={(e) => handleSaveConfig('contact_form_badge', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="contact_form_title">Título</Label>
                  <Input
                    id="contact_form_title"
                    defaultValue={getConfig('contact_form_title', 'Quick Contact Form')}
                    onBlur={(e) => handleSaveConfig('contact_form_title', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="contact_form_description">Descripción</Label>
                  <Textarea
                    id="contact_form_description"
                    defaultValue={getConfig('contact_form_description', '')}
                    onBlur={(e) => handleSaveConfig('contact_form_description', e.target.value)}
                    rows={2}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Áreas de Servicio</CardTitle>
                <CardDescription>Edita la sección de áreas de servicio</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="contact_areas_badge">Badge</Label>
                  <Input
                    id="contact_areas_badge"
                    defaultValue={getConfig('contact_areas_badge', 'Service Areas')}
                    onBlur={(e) => handleSaveConfig('contact_areas_badge', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="contact_areas_title">Título</Label>
                  <Input
                    id="contact_areas_title"
                    defaultValue={getConfig('contact_areas_title', 'Where We Operate')}
                    onBlur={(e) => handleSaveConfig('contact_areas_title', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="contact_areas_description">Descripción</Label>
                  <Textarea
                    id="contact_areas_description"
                    defaultValue={getConfig('contact_areas_description', '')}
                    onBlur={(e) => handleSaveConfig('contact_areas_description', e.target.value)}
                    rows={2}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Línea de Emergencia</CardTitle>
                <CardDescription>Edita la sección de emergencias 24/7</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="contact_emergency_title">Título</Label>
                  <Input
                    id="contact_emergency_title"
                    defaultValue={getConfig('contact_emergency_title', '24/7 Emergency Line')}
                    onBlur={(e) => handleSaveConfig('contact_emergency_title', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="contact_emergency_description">Descripción</Label>
                  <Textarea
                    id="contact_emergency_description"
                    defaultValue={getConfig('contact_emergency_description', '')}
                    onBlur={(e) => handleSaveConfig('contact_emergency_description', e.target.value)}
                    rows={2}
                  />
                </div>
                <div>
                  <Label htmlFor="contact_emergency_text">Texto Adicional</Label>
                  <Textarea
                    id="contact_emergency_text"
                    defaultValue={getConfig('contact_emergency_text', '')}
                    onBlur={(e) => handleSaveConfig('contact_emergency_text', e.target.value)}
                    rows={2}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Redes Sociales</CardTitle>
                <CardDescription>Edita la sección de redes sociales</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="contact_social_badge">Badge</Label>
                  <Input
                    id="contact_social_badge"
                    defaultValue={getConfig('contact_social_badge', 'Follow Us')}
                    onBlur={(e) => handleSaveConfig('contact_social_badge', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="contact_social_title">Título</Label>
                  <Input
                    id="contact_social_title"
                    defaultValue={getConfig('contact_social_title', 'Stay Connected')}
                    onBlur={(e) => handleSaveConfig('contact_social_title', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="contact_social_description">Descripción</Label>
                  <Textarea
                    id="contact_social_description"
                    defaultValue={getConfig('contact_social_description', '')}
                    onBlur={(e) => handleSaveConfig('contact_social_description', e.target.value)}
                    rows={2}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default AdminPageContent;
